// src/components/Route/Route.jsx
import React, { useEffect, useState } from 'react';

import routeService from '../../services/routeeService';

import RouteTable from './RouteeTable';
import RouteModal from './RouteeModal';
import EditRouteModal from './EditRouteeModal';

import { showNotification } from '../../utils/notification';
import { PlusCircle } from 'lucide-react';
import '../Inventory/inventory.css';

const Routee = ({ toggleSidebar }) => {
  const [routes, setRoutes] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedRoute, setSelectedRoute] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [filters, setFilters] = useState({ search: '' });

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadRoutes();
  }, [page]);

  useEffect(() => {
    applyFilters();
  }, [routes, filters]);

  const loadRoutes = async () => {
    try {
      setIsLoading(true);
      const data = await routeService.getAll(page, limit);
      setHasNextPage(data.length === limit);
      
      // Estimate total pages
      if (data.length < limit) {
        setTotalPages(page);
      } else {
        setTotalPages(page + 1);
      }
      
      setRoutes(data);
      showNotification(`Loaded ${data.length} routes`, 'success');
    } catch (error) {
      console.error('Error loading routes:', error);
      showNotification(`Failed to load routes: ${error.message}`, 'error');
      setRoutes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    const safeRoutes = Array.isArray(routes) ? routes : [];
    let filtered = [...safeRoutes];

    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((r) => {
        const name = r.name?.toLowerCase() || '';
        const description = r.description?.toLowerCase() || '';
        return name.includes(searchLower) || description.includes(searchLower);
      });
    }

    setFilteredRoutes(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ search: '' });
    showNotification('Filters cleared', 'info');
  };

  const handleAddNew = () => {
    setSelectedRoute(null);
    setIsModalOpen(true);
  };

  const handleEdit = (route) => {
    setSelectedRoute(route);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRoute(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedRoute(null);
  };

  // POST /routes/create (bulk)
  const handleSave = async (payload) => {
    try {
      setIsSaving(true);
      await routeService.create(payload);
      showNotification('New routes added successfully!', 'success');
      setPage(1);
      setIsModalOpen(false);
      await loadRoutes();
    } catch (error) {
      console.error('Error saving routes:', error);
      showNotification(`Failed to save: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // PUT /routes/edit/:id (single)
  const handleSaveEdits = async (formData) => {
    try {
      setIsSavingEdit(true);

      const payload = {
        name: formData.name,
        isActive: formData.isActive,
      };

      await routeService.update(selectedRoute.id, payload);
      showNotification(`Route "${payload.name}" updated successfully!`, 'success');

      await loadRoutes();
      handleCloseEditModal();
    } catch (error) {
      console.error('Edit failed:', error);
      showNotification(`Failed to update: ${error.message}`, 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // DELETE /delete/:id
  const handleDelete = async (route) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the route: ${route.name}?`
    );
    if (!confirmDelete) return;

    try {
      await routeService.delete(route.id);
      showNotification('Route deleted successfully', 'success');
      setPage(1);
    } catch (error) {
      console.error('Error deleting route:', error);
      showNotification(`Failed to delete: ${error.message}`, 'error');
    }
  };

  // ========== PAGINATION HELPERS ==========
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <main className="inventory-main-content">
      <header className="inventory-content-header">
        <button onClick={toggleSidebar} className="sidebar-toggle-btn" type="button">
          ☰
        </button>

        <h2 className="inventory-content-title">Route Management</h2>

        <div className="header-actions">
          <button
            className="inventory-btn inventory-btn-add"
            onClick={handleAddNew}
            type="button"
          >
            <PlusCircle size={18} />
            Add New Route
          </button>
        </div>
      </header>

      <section className="inventory-filter-controls">
        <div className="inventory-filter-group">
          <input
            type="text"
            name="search"
            className="inventory-custom-input inventory-search-input"
            placeholder="Search Route Name or Description"
            value={filters.search}
            onChange={handleFilterChange}
          />

          {/* <button
            className="inventory-btn inventory-btn-primary"
            onClick={applyFilters}
            type="button"
          >
            Apply Filter
          </button> */}

          <button
            className="inventory-btn inventory-btn-clear"
            onClick={handleClearFilters}
            type="button"
          >
            Clear Filters
          </button>
        </div>
      </section>

      <RouteTable
        routees={filteredRoutes}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isLoading={isLoading}
      />

      {/* Pagination Controls */}
      <div className="pagination-controls">
        <button
          className="btn-page"
          disabled={page === 1}
          onClick={() => setPage((prev) => prev - 1)}
          type="button"
        >
          Previous
        </button>
        
        <div className="page-numbers">
          {page > 2 && (
            <>
              <button className="btn-page-num" onClick={() => handlePageChange(1)}>1</button>
              {page > 3 && <span className="page-ellipsis">...</span>}
            </>
          )}
          
          {page > 1 && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(page - 1)}
            >
              {page - 1}
            </button>
          )}
          
          <button className="btn-page-num active">{page}</button>
          
          {hasNextPage && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(page + 1)}
            >
              {page + 1}
            </button>
          )}
          
          {hasNextPage && page < totalPages - 1 && (
            <>
              {page < totalPages - 2 && <span className="page-ellipsis">...</span>}
              <button className="btn-page-num" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
            </>
          )}
        </div>

        <span className="page-info">Page {page} of {totalPages || 1}</span>

        <button
          className="btn-page"
          disabled={!hasNextPage}
          onClick={() => setPage((prev) => prev + 1)}
          type="button"
        >
          Next
        </button>
      </div>

      <RouteModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <EditRouteModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveEdits}
        route={selectedRoute}
        isSaving={isSavingEdit}
      />
    </main>
  );
};

export default Routee;